/**
 * SSE（Server-Sent Events）流解析工具。
 *
 * 后端各流式接口以标准 SSE 帧输出：事件之间用空行（\n\n）分隔，
 * 每帧由 `event:` / `data:` 等字段行组成。前端在 ReadableStream 读取过程中
 * 会不断累积字节到 buffer，本工具从 buffer 中切出【已完整】的事件，
 * 返回解析后的事件列表与尚未构成完整事件的剩余片段（remaining），
 * 供下一次读取继续拼接，避免半个事件被错误解析。
 */

export interface SseEvent {
  /** 事件名（无 event: 字段时默认为 "message"） */
  event: string;
  /** 事件数据（多行 data: 以换行拼接） */
  data: string;
}

export interface SseParseResult {
  events: SseEvent[];
  /** 不构成完整事件的尾部，保留到下次拼接 */
  remaining: string;
}

/**
 * 从 SSE 文本缓冲区中提取所有已完整的事件。
 *
 * @param buffer 累积的原始 SSE 文本
 * @returns 解析出的事件列表 + 剩余未完成片段
 */
export function extractSseEvents(buffer: string): SseParseResult {
  const events: SseEvent[] = [];

  if (!buffer) {
    return { events, remaining: '' };
  }

  // 统一换行；事件以空行分隔
  const normalized = buffer.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const lastSep = normalized.lastIndexOf('\n\n');
  if (lastSep === -1) {
    // 还没有任何完整事件
    return { events, remaining: normalized };
  }

  const complete = normalized.slice(0, lastSep);
  const remaining = normalized.slice(lastSep + 2);

  for (const block of complete.split('\n\n')) {
    if (!block) {
      continue;
    }
    let eventName = 'message';
    const dataLines: string[] = [];

    for (const rawLine of block.split('\n')) {
      const line = rawLine;
      if (line === '' || line.startsWith(':')) {
        // 空行或注释行（: 开头）忽略
        continue;
      }
      const colon = line.indexOf(':');
      const field = colon === -1 ? line : line.slice(0, colon);
      // 字段值去掉冒号后一个可选空格
      let value = colon === -1 ? '' : line.slice(colon + 1);
      if (value.startsWith(' ')) {
        value = value.slice(1);
      }
      if (field === 'event') {
        eventName = value;
      } else if (field === 'data') {
        dataLines.push(value);
      }
      // 其它字段（id/retry 等）当前不需要，忽略
    }

    // 仅在该块确实包含 data/event 字段时才作为事件输出
    if (dataLines.length > 0 || eventName !== 'message') {
      events.push({ event: eventName, data: dataLines.join('\n') });
    }
  }

  return { events, remaining };
}
